import { useState, useEffect } from 'react';
import { Sidebar } from '../../../components/feature/Sidebar';
import { getMockAnimals, getMockMilkingData, getMockFeedData } from '../../../data/farmtraceMockData';

interface ProductionCost {
    category: string;
    amount: number;
    unit: string;
    costPerUnit: number;
    total: number;
    icon: string;
    color: string;
}

interface MilkQualityMetrics {
    protein: number;
    fat: number;
    ufc: number;
    somaticCells: number;
    qualityScore: number;
}

interface ProfitabilityData {
    productorId: string;
    productorName: string;
    volume: number; // litros
    quality: MilkQualityMetrics;
    basePricePerLiter: number;
    adjustedPricePerLiter: number;
    costs: ProductionCost[];
    totalCosts: number;
    costPerLiter: number;
    revenue: number;
    profit: number;
    profitMargin: number;
}

export default function FarmTraceProfitability() {
    const [loading, setLoading] = useState(true);
    const [profitabilityData, setProfitabilityData] = useState<ProfitabilityData[]>([]);
    const [selectedProductor, setSelectedProductor] = useState<ProfitabilityData | null>(null);
    const [timeRange, setTimeRange] = useState<'daily' | 'weekly' | 'monthly'>('daily');

    useEffect(() => {
        loadProfitabilityData();
    }, [timeRange]);

    const loadProfitabilityData = async () => {
        try {
            setLoading(true);
            const [animalsRes, milkingRes, feedRes] = await Promise.allSettled([
                getMockAnimals(),
                getMockMilkingData(7),
                getMockFeedData(7),
            ]);

            const animalsData = animalsRes.status === 'fulfilled' ? extractArray(animalsRes.value) : [];
            const milkingData = milkingRes.status === 'fulfilled' ? extractArray(milkingRes.value) : [];
            const feedData = feedRes.status === 'fulfilled' ? extractArray(feedRes.value) : [];

            // Simular datos de rentabilidad por productora
            const profitData: ProfitabilityData[] = [
                {
                    productorId: 'PROD-001',
                    productorName: 'Productora Norte',
                    volume: 2500,
                    quality: {
                        protein: 3.2,
                        fat: 3.8,
                        ufc: 150000,
                        somaticCells: 180000,
                        qualityScore: 92,
                    },
                    basePricePerLiter: 0.35,
                    adjustedPricePerLiter: 0.38,
                    costs: [
                        {
                            category: 'Alimentación',
                            amount: 1200,
                            unit: 'kg',
                            costPerUnit: 0.40,
                            total: 480,
                            icon: 'ri-restaurant-line',
                            color: 'text-orange-600',
                        },
                        {
                            category: 'Agua',
                            amount: 15000,
                            unit: 'L',
                            costPerUnit: 0.002,
                            total: 30,
                            icon: 'ri-drop-line',
                            color: 'text-blue-600',
                        },
                        {
                            category: 'Electricidad',
                            amount: 450,
                            unit: 'kWh',
                            costPerUnit: 0.15,
                            total: 67.5,
                            icon: 'ri-flashlight-line',
                            color: 'text-yellow-600',
                        },
                        {
                            category: 'Envasado',
                            amount: 2500,
                            unit: 'L',
                            costPerUnit: 0.05,
                            total: 125,
                            icon: 'ri-box-3-line',
                            color: 'text-purple-600',
                        },
                        {
                            category: 'Transporte',
                            amount: 1,
                            unit: 'viaje',
                            costPerUnit: 80,
                            total: 80,
                            icon: 'ri-truck-line',
                            color: 'text-green-600',
                        },
                    ],
                    totalCosts: 782.5,
                    costPerLiter: 0.313,
                    revenue: 950,
                    profit: 167.5,
                    profitMargin: 17.6,
                },
                {
                    productorId: 'PROD-002',
                    productorName: 'Productora Sur',
                    volume: 1800,
                    quality: {
                        protein: 3.0,
                        fat: 3.5,
                        ufc: 200000,
                        somaticCells: 220000,
                        qualityScore: 85,
                    },
                    basePricePerLiter: 0.35,
                    adjustedPricePerLiter: 0.36,
                    costs: [
                        {
                            category: 'Alimentación',
                            amount: 900,
                            unit: 'kg',
                            costPerUnit: 0.40,
                            total: 360,
                            icon: 'ri-restaurant-line',
                            color: 'text-orange-600',
                        },
                        {
                            category: 'Agua',
                            amount: 11000,
                            unit: 'L',
                            costPerUnit: 0.002,
                            total: 22,
                            icon: 'ri-drop-line',
                            color: 'text-blue-600',
                        },
                        {
                            category: 'Electricidad',
                            amount: 320,
                            unit: 'kWh',
                            costPerUnit: 0.15,
                            total: 48,
                            icon: 'ri-flashlight-line',
                            color: 'text-yellow-600',
                        },
                        {
                            category: 'Envasado',
                            amount: 1800,
                            unit: 'L',
                            costPerUnit: 0.05,
                            total: 90,
                            icon: 'ri-box-3-line',
                            color: 'text-purple-600',
                        },
                        {
                            category: 'Transporte',
                            amount: 1,
                            unit: 'viaje',
                            costPerUnit: 70,
                            total: 70,
                            icon: 'ri-truck-line',
                            color: 'text-green-600',
                        },
                    ],
                    totalCosts: 590,
                    costPerLiter: 0.328,
                    revenue: 648,
                    profit: 58,
                    profitMargin: 8.9,
                },
                {
                    productorId: 'PROD-003',
                    productorName: 'Productora Este',
                    volume: 3200,
                    quality: {
                        protein: 3.4,
                        fat: 4.0,
                        ufc: 120000,
                        somaticCells: 150000,
                        qualityScore: 96,
                    },
                    basePricePerLiter: 0.35,
                    adjustedPricePerLiter: 0.40,
                    costs: [
                        {
                            category: 'Alimentación',
                            amount: 1500,
                            unit: 'kg',
                            costPerUnit: 0.40,
                            total: 600,
                            icon: 'ri-restaurant-line',
                            color: 'text-orange-600',
                        },
                        {
                            category: 'Agua',
                            amount: 19000,
                            unit: 'L',
                            costPerUnit: 0.002,
                            total: 38,
                            icon: 'ri-drop-line',
                            color: 'text-blue-600',
                        },
                        {
                            category: 'Electricidad',
                            amount: 580,
                            unit: 'kWh',
                            costPerUnit: 0.15,
                            total: 87,
                            icon: 'ri-flashlight-line',
                            color: 'text-yellow-600',
                        },
                        {
                            category: 'Envasado',
                            amount: 3200,
                            unit: 'L',
                            costPerUnit: 0.05,
                            total: 160,
                            icon: 'ri-box-3-line',
                            color: 'text-purple-600',
                        },
                        {
                            category: 'Transporte',
                            amount: 1,
                            unit: 'viaje',
                            costPerUnit: 100,
                            total: 100,
                            icon: 'ri-truck-line',
                            color: 'text-green-600',
                        },
                    ],
                    totalCosts: 985,
                    costPerLiter: 0.308,
                    revenue: 1280,
                    profit: 295,
                    profitMargin: 23.0,
                },
            ];

            setProfitabilityData(profitData);
            setSelectedProductor(profitData[0]);
        } catch (error) {
            console.error('Error loading profitability data:', error);
        } finally {
            setLoading(false);
        }
    };

    const extractArray = (response: any): any[] => {
        if (Array.isArray(response)) return response;
        if (response?.items) return response.items;
        if (response?.member) return response.member;
        if (response?.data) return response.data;
        return [];
    };

    const calculateQualityAdjustment = (quality: MilkQualityMetrics) => {
        const basePrice = 0.35;
        let adjustment = 0;

        // Ajuste por proteína
        if (quality.protein >= 3.3) adjustment += 0.03;
        else if (quality.protein >= 3.0) adjustment += 0.01;

        // Ajuste por grasa
        if (quality.fat >= 3.8) adjustment += 0.02;
        else if (quality.fat >= 3.5) adjustment += 0.01;

        // Penalización por UFC alto
        if (quality.ufc > 200000) adjustment -= 0.02;
        else if (quality.ufc > 150000) adjustment -= 0.01;

        // Penalización por células somáticas altas
        if (quality.somaticCells > 250000) adjustment -= 0.02;
        else if (quality.somaticCells > 200000) adjustment -= 0.01;

        return basePrice + adjustment;
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="inline-block w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-4"></div>
                    <p className="text-gray-700 text-lg font-medium">Cargando análisis de rentabilidad...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex">
            <Sidebar />

            <div className="flex-1 flex flex-col overflow-hidden ml-[var(--sidebar-width)] transition-all duration-300">
                {/* Top Bar */}
                <div className="bg-white/80 backdrop-blur-xl border-b border-gray-200 px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">Análisis de Rentabilidad</h1>
                            <p className="text-sm text-gray-600 mt-1">Costos, calidad y márgenes por productora</p>
                        </div>
                        <div className="flex items-center gap-3">
                            <select
                                value={timeRange}
                                onChange={(e) => setTimeRange(e.target.value as any)}
                                className="px-4 py-2 border border-gray-200 rounded-xl text-sm font-medium focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                                <option value="daily">Diario</option>
                                <option value="weekly">Semanal</option>
                                <option value="monthly">Mensual</option>
                            </select>
                            <button
                                onClick={loadProfitabilityData}
                                className="p-3 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-xl hover:from-blue-600 hover:to-indigo-600 transition-all duration-200 shadow-lg"
                                title="Actualizar datos"
                            >
                                <i className="ri-refresh-line text-lg"></i>
                            </button>
                        </div>
                    </div>
                </div>

                {/* Main Content */}
                <div className="flex-1 overflow-y-auto p-8">
                    {/* Summary Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
                        <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-gray-600 mb-1">Producción Total</p>
                                    <p className="text-3xl font-bold text-blue-600">
                                        {profitabilityData.reduce((sum, p) => sum + p.volume, 0).toLocaleString()}L
                                    </p>
                                </div>
                                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                                    <i className="ri-drop-line text-blue-600 text-2xl"></i>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-gray-600 mb-1">Ingresos Totales</p>
                                    <p className="text-3xl font-bold text-green-600">
                                        ${profitabilityData.reduce((sum, p) => sum + p.revenue, 0).toLocaleString()}
                                    </p>
                                </div>
                                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                                    <i className="ri-money-dollar-circle-line text-green-600 text-2xl"></i>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-gray-600 mb-1">Costos Totales</p>
                                    <p className="text-3xl font-bold text-orange-600">
                                        ${profitabilityData.reduce((sum, p) => sum + p.totalCosts, 0).toLocaleString()}
                                    </p>
                                </div>
                                <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                                    <i className="ri-wallet-line text-orange-600 text-2xl"></i>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-gray-600 mb-1">Ganancia Neta</p>
                                    <p className="text-3xl font-bold text-purple-600">
                                        ${profitabilityData.reduce((sum, p) => sum + p.profit, 0).toLocaleString()}
                                    </p>
                                </div>
                                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                                    <i className="ri-line-chart-line text-purple-600 text-2xl"></i>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Productoras Comparison */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
                        {profitabilityData.map((productor) => (
                            <div
                                key={productor.productorId}
                                onClick={() => setSelectedProductor(productor)}
                                className={`bg-white rounded-2xl shadow-lg p-6 border-2 cursor-pointer transition-all duration-200 hover:shadow-xl ${selectedProductor?.productorId === productor.productorId
                                    ? 'border-blue-500 ring-2 ring-blue-200'
                                    : 'border-gray-100 hover:border-blue-300'
                                    }`}
                            >
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="text-lg font-bold text-gray-900">{productor.productorName}</h3>
                                    <span className="text-xs text-gray-500">{productor.productorId}</span>
                                </div>

                                <div className="space-y-3">
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-gray-600">Volumen</span>
                                        <span className="text-sm font-bold text-blue-600">{productor.volume.toLocaleString()}L</span>
                                    </div>

                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-gray-600">Calidad</span>
                                        <div className="flex items-center gap-1">
                                            <div className="w-16 bg-gray-200 rounded-full h-2">
                                                <div
                                                    className="bg-gradient-to-r from-green-500 to-emerald-500 h-2 rounded-full"
                                                    style={{ width: `${productor.quality.qualityScore}%` }}
                                                ></div>
                                            </div>
                                            <span className="text-sm font-bold text-green-600">{productor.quality.qualityScore}%</span>
                                        </div>
                                    </div>

                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-gray-600">Precio/L</span>
                                        <span className="text-sm font-bold text-gray-900">
                                            ${productor.adjustedPricePerLiter.toFixed(2)}
                                        </span>
                                    </div>

                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-gray-600">Costo/L</span>
                                        <span className="text-sm font-bold text-orange-600">
                                            ${productor.costPerLiter.toFixed(2)}
                                        </span>
                                    </div>

                                    <div className="pt-3 border-t border-gray-200">
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm font-semibold text-gray-700">Margen</span>
                                            <span
                                                className={`text-lg font-bold ${productor.profitMargin > 20
                                                    ? 'text-green-600'
                                                    : productor.profitMargin > 10
                                                        ? 'text-yellow-600'
                                                        : 'text-red-600'
                                                    }`}
                                            >
                                                {productor.profitMargin.toFixed(1)}%
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Detailed Analysis */}
                    {selectedProductor && (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Quality Metrics */}
                            <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
                                <h2 className="text-lg font-bold text-gray-900 mb-4">Métricas de Calidad</h2>
                                <div className="space-y-4">
                                    <div>
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="text-sm text-gray-600">Proteína</span>
                                            <span className="text-sm font-bold text-gray-900">
                                                {selectedProductor.quality.protein.toFixed(1)}%
                                            </span>
                                        </div>
                                        <div className="w-full bg-gray-200 rounded-full h-2">
                                            <div
                                                className="bg-gradient-to-r from-blue-500 to-indigo-500 h-2 rounded-full"
                                                style={{ width: `${(selectedProductor.quality.protein / 4) * 100}%` }}
                                            ></div>
                                        </div>
                                    </div>

                                    <div>
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="text-sm text-gray-600">Grasa</span>
                                            <span className="text-sm font-bold text-gray-900">
                                                {selectedProductor.quality.fat.toFixed(1)}%
                                            </span>
                                        </div>
                                        <div className="w-full bg-gray-200 rounded-full h-2">
                                            <div
                                                className="bg-gradient-to-r from-yellow-500 to-orange-500 h-2 rounded-full"
                                                style={{ width: `${(selectedProductor.quality.fat / 5) * 100}%` }}
                                            ></div>
                                        </div>
                                    </div>

                                    <div>
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="text-sm text-gray-600">UFC</span>
                                            <span className="text-sm font-bold text-gray-900">
                                                {selectedProductor.quality.ufc.toLocaleString()}
                                            </span>
                                        </div>
                                        <div className="w-full bg-gray-200 rounded-full h-2">
                                            <div
                                                className={`h-2 rounded-full ${selectedProductor.quality.ufc < 150000
                                                    ? 'bg-gradient-to-r from-green-500 to-emerald-500'
                                                    : selectedProductor.quality.ufc < 200000
                                                        ? 'bg-gradient-to-r from-yellow-500 to-orange-500'
                                                        : 'bg-gradient-to-r from-red-500 to-pink-500'
                                                    }`}
                                                style={{ width: `${Math.min((selectedProductor.quality.ufc / 300000) * 100, 100)}%` }}
                                            ></div>
                                        </div>
                                    </div>

                                    <div>
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="text-sm text-gray-600">Células Somáticas</span>
                                            <span className="text-sm font-bold text-gray-900">
                                                {selectedProductor.quality.somaticCells.toLocaleString()}
                                            </span>
                                        </div>
                                        <div className="w-full bg-gray-200 rounded-full h-2">
                                            <div
                                                className={`h-2 rounded-full ${selectedProductor.quality.somaticCells < 200000
                                                    ? 'bg-gradient-to-r from-green-500 to-emerald-500'
                                                    : selectedProductor.quality.somaticCells < 250000
                                                        ? 'bg-gradient-to-r from-yellow-500 to-orange-500'
                                                        : 'bg-gradient-to-r from-red-500 to-pink-500'
                                                    }`}
                                                style={{
                                                    width: `${Math.min((selectedProductor.quality.somaticCells / 400000) * 100, 100)}%`,
                                                }}
                                            ></div>
                                        </div>
                                    </div>

                                    <div className="pt-4 border-t border-gray-200">
                                        <div className="flex items-center justify-between p-4 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl">
                                            <div>
                                                <p className="text-sm text-gray-600 mb-1">Precio Base</p>
                                                <p className="text-2xl font-bold text-gray-900">
                                                    ${selectedProductor.basePricePerLiter.toFixed(2)}
                                                </p>
                                            </div>
                                            <div className="text-center">
                                                <i className="ri-arrow-right-line text-2xl text-gray-400"></i>
                                            </div>
                                            <div>
                                                <p className="text-sm text-gray-600 mb-1">Precio Ajustado</p>
                                                <p className="text-2xl font-bold text-green-600">
                                                    ${selectedProductor.adjustedPricePerLiter.toFixed(2)}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Cost Breakdown */}
                            <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
                                <h2 className="text-lg font-bold text-gray-900 mb-4">Desglose de Costos</h2>
                                <div className="space-y-3">
                                    {selectedProductor.costs.map((cost, index) => (
                                        <div
                                            key={index}
                                            className="p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
                                        >
                                            <div className="flex items-center justify-between mb-2">
                                                <div className="flex items-center gap-3">
                                                    <div className={`w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm`}>
                                                        <i className={`${cost.icon} ${cost.color} text-xl`}></i>
                                                    </div>
                                                    <div>
                                                        <p className="text-sm font-semibold text-gray-900">{cost.category}</p>
                                                        <p className="text-xs text-gray-500">
                                                            {cost.amount.toLocaleString()} {cost.unit} × ${cost.costPerUnit.toFixed(2)}
                                                        </p>
                                                    </div>
                                                </div>
                                                <p className="text-lg font-bold text-gray-900">${cost.total.toFixed(2)}</p>
                                            </div>
                                            <div className="w-full bg-gray-200 rounded-full h-1.5">
                                                <div
                                                    className="bg-gradient-to-r from-blue-500 to-indigo-500 h-1.5 rounded-full"
                                                    style={{ width: `${(cost.total / selectedProductor.totalCosts) * 100}%` }}
                                                ></div>
                                            </div>
                                        </div>
                                    ))}

                                    <div className="pt-4 border-t-2 border-gray-200">
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="text-sm font-semibold text-gray-700">Costo Total</span>
                                            <span className="text-xl font-bold text-orange-600">
                                                ${selectedProductor.totalCosts.toFixed(2)}
                                            </span>
                                        </div>
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="text-sm font-semibold text-gray-700">Costo por Litro</span>
                                            <span className="text-xl font-bold text-orange-600">
                                                ${selectedProductor.costPerLiter.toFixed(3)}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="pt-4 border-t-2 border-gray-200">
                                        <div className="p-4 bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl">
                                            <div className="flex items-center justify-between mb-2">
                                                <span className="text-sm font-semibold text-gray-700">Ingresos</span>
                                                <span className="text-xl font-bold text-green-600">
                                                    ${selectedProductor.revenue.toFixed(2)}
                                                </span>
                                            </div>
                                            <div className="flex items-center justify-between mb-2">
                                                <span className="text-sm font-semibold text-gray-700">Ganancia</span>
                                                <span className="text-2xl font-bold text-purple-600">
                                                    ${selectedProductor.profit.toFixed(2)}
                                                </span>
                                            </div>
                                            <div className="flex items-center justify-between">
                                                <span className="text-sm font-semibold text-gray-700">Margen</span>
                                                <span className="text-2xl font-bold text-purple-600">
                                                    {selectedProductor.profitMargin.toFixed(1)}%
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
